/**
 * What a leaderboard surface renders, decided once for both clients.
 *
 * Four decisions used to be made twice: which board to open, which rows to
 * show, whether the reader needs a separate row because they rank below those
 * rows, and whether "show more" leads anywhere. Web made them inline in its
 * detail page, mobile made them in a `leaderboardDisplay` helper, and they had
 * already drifted: web preferred a company board and mobile took whichever came
 * first, so one member could open two different boards on two devices.
 *
 * Which board is now the server's answer (`primaryInstanceId`), because only
 * the server knows what exists. The rest are pure functions of the payload and
 * live here, so a client renders and nothing more.
 */
const EMPTY_VIEW = {
    board: null,
    entries: [],
    viewerRow: null,
    canShowMore: false,
    groupEntries: [],
};
/** The board a surface opens on, as chosen by the server. */
export function primaryBoard(response) {
    if (!response)
        return null;
    return (response.leaderboards.find((board) => board.instanceId === response.primaryInstanceId) ?? null);
}
/** The weekend podium's board. It has its own surface and its own empty state. */
export function weekendBoard(response) {
    return (response?.leaderboards.find((board) => board.scope === "company" && board.periodKind === "weekend") ?? null);
}
export function leaderboardView(response, options = {}) {
    const board = options.instanceId
        ? (response?.leaderboards.find((entry) => entry.instanceId === options.instanceId) ??
            primaryBoard(response))
        : primaryBoard(response);
    if (!board)
        return EMPTY_VIEW;
    const entries = [...(options.pageEntries ?? board.entries)];
    const mine = board.myEntry;
    return {
        board,
        entries,
        viewerRow: mine && !entries.some((entry) => entry.userId === mine.userId) ? mine : null,
        canShowMore: options.pageHasMore ?? board.hasMore,
        groupEntries: board.groupEntries,
    };
}
/**
 * The short standings list beside the company challenge card: the leaders, but
 * never without the reader. A member ranked below the slice used to vanish from
 * their own standings, so when they are not already in it their row replaces
 * the last leader.
 */
export function challengeStandingsTop(response, limit = 5) {
    const board = primaryBoard(response);
    if (!board)
        return [];
    return boardStandingsTop(board, limit);
}
/**
 * The same rule for one board the caller already holds: at most `limit` rows,
 * leaders first, and the reader's own row in the last slot (with their real
 * rank and score) when they finished below the cut. Past challenges draw their
 * final boards this way, since a settled standing has no pager to scroll.
 */
export function boardStandingsTop(board, limit) {
    const leaders = board.entries.slice(0, limit);
    const mine = board.myEntry;
    if (!mine || leaders.some((entry) => entry.userId === mine.userId))
        return leaders;
    return [...board.entries.slice(0, Math.max(0, limit - 1)), mine];
}
export function leaderboardColumns(response) {
    const boards = response?.leaderboards ?? [];
    const company = boards.filter((board) => board.scope === "company");
    const challenge = boards.find((board) => board.scope === "route");
    const period = company.find((board) => board.periodKind !== "weekend");
    const weekend = company.find((board) => board.periodKind === "weekend");
    return [
        challenge ? { key: "challenge", eyebrow: "Total challenge", board: challenge } : null,
        period
            ? { key: "period", eyebrow: periodEyebrow(period.periodKind), board: period }
            : null,
        weekend ? { key: "weekend", eyebrow: "Last weekend", board: weekend } : null,
    ].filter((column) => column !== null);
}
function periodEyebrow(periodKind) {
    return periodKind === "month" ? "This month" : "This week";
}
/**
 * The board whose totals head the page. The whole challenge when there is one,
 * since that is the number a company is actually moving together, otherwise
 * whichever board the surface opened on.
 */
export function headlineBoard(response) {
    const columns = leaderboardColumns(response);
    return columns.find((column) => column.key === "challenge")?.board ?? primaryBoard(response);
}
/** Group standings, taken from whichever compared board carries them. */
export function groupStandings(response) {
    const withGroups = leaderboardColumns(response).find((column) => column.board.groupEntries.length);
    return withGroups
        ? { entries: withGroups.board.groupEntries, instanceId: withGroups.board.instanceId }
        : { entries: [], instanceId: null };
}
export function recognitionCallouts(response) {
    return recognitionCalloutsFromEntries(response?.recognition);
}
/**
 * The same callouts from bare recognition entries, for surfaces that carry
 * them outside the live leaderboards response. Captions default to the live
 * present-tense copy; a past surface overrides the ones that say "this week".
 */
export function recognitionCalloutsFromEntries(entries, captionOverrides) {
    return (entries ?? []).map((entry) => ({
        kind: entry.kind,
        label: RECOGNITION_LABELS[entry.kind],
        member: {
            userId: entry.userId,
            displayName: entry.displayName,
            initials: entry.initials,
            avatarUrl: entry.avatarUrl,
            avatarColor: entry.avatarColor,
        },
        value: entry.kind === "best_day"
            ? `${entry.value.toLocaleString()} steps`
            : `${entry.value} ${entry.value === 1 ? "day" : "days"}`,
        caption: captionOverrides?.[entry.kind] ?? RECOGNITION_CAPTIONS[entry.kind],
    }));
}
const RECOGNITION_LABELS = {
    most_consistent: "Most consistent",
    longest_streak: "Longest streak",
    best_day: "Biggest day",
};
const RECOGNITION_CAPTIONS = {
    most_consistent: "on target this week",
    longest_streak: "in a row",
    best_day: "in one day",
};
//# sourceMappingURL=leaderboard-view.js.map